import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, Plus, Trash2 } from 'lucide-react';
import { iconOptionsStandFor, iconOptionsCoverage } from './homeDefaults';
import { defaultActivitiesSlides } from '@/components/ActivitiesCarousel';
import { defaultCoverageContent } from '@/components/CommitmentSlider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultDivider = {
  enabled: true,
  height_px: 2,
  color_1: '#f97316',
  color_2: '#60a5fa',
  color_3: '#4ade80',
};

const defaultStandFor = {
  heading: 'What We Stand For',
  subheading:
    'Our mission is to protect, represent, and empower public sector workers across Papua New Guinea.',
  cards: [
    {
      icon: 'Shield',
      title: "Workers' Rights",
      body_markdown:
        'Advocating for fair treatment, job security, and protection against unfair dismissal or discrimination.',
    },
    {
      icon: 'DollarSign',
      title: 'Fair Wages',
      body_markdown:
        'Negotiating competitive salaries and benefits that reflect the value and dedication of public employees.',
    },
    {
      icon: 'TrendingUp',
      title: 'Development',
      body_markdown:
        'Providing training, resources, and opportunities for professional growth and career advancement.',
    },
  ],
};

const defaultCTA = {
  heading: 'Ready to Join Our Community?',
  body_markdown:
    'Become part of the largest public employees association in Papua New Guinea and enjoy exclusive benefits.',
  button_text: 'Join Now',
  button_link: '/membership',
};

const HomeSectionsEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [divider, setDivider] = useState(defaultDivider);
  const [activities, setActivities] = useState({ interval_ms: 4000, slides: defaultActivitiesSlides });
  const [standFor, setStandFor] = useState(defaultStandFor);
  const [coverage, setCoverage] = useState(defaultCoverageContent);
  const [cta, setCTA] = useState(defaultCTA);

  const token = useMemo(() => localStorage.getItem('admin_token'), []);

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/page-content/home`);
      res.data.forEach((item) => {
        if (item.section_name === 'divider') setDivider({ ...defaultDivider, ...item.content });
        if (item.section_name === 'activities') setActivities(item.content);
        if (item.section_name === 'stand_for') setStandFor(item.content);
        if (item.section_name === 'coverage') setCoverage(item.content);
        if (item.section_name === 'cta') setCTA(item.content);
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();


  }, []);

  const saveSection = async (sectionName, content) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/page-content/home/${sectionName}`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Saved successfully');
    } catch (e) {
      console.error(e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Divider Line (Hero → Activities)</CardTitle>
          <CardDescription>Enable/disable and adjust colors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Enabled</Label>
              <select
                value={divider.enabled ? 'yes' : 'no'}
                onChange={(e) => setDivider({ ...divider, enabled: e.target.value === 'yes' })}
                className="w-full px-3 py-2 border rounded-md mt-1"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <Label>Height (px)</Label>
              <Input
                type="number"
                value={divider.height_px}
                onChange={(e) => setDivider({ ...divider, height_px: parseInt(e.target.value || '2') })}
                className="mt-1"
                min={1}
                max={8}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Color 1</Label>
              <Input
                value={divider.color_1}
                onChange={(e) => setDivider({ ...divider, color_1: e.target.value })}
                className="mt-1"
                placeholder="#f97316"
              />
            </div>
            <div>
              <Label>Color 2</Label>
              <Input
                value={divider.color_2}
                onChange={(e) => setDivider({ ...divider, color_2: e.target.value })}
                className="mt-1"
                placeholder="#60a5fa"
              />
            </div>
            <div>
              <Label>Color 3</Label>
              <Input
                value={divider.color_3}
                onChange={(e) => setDivider({ ...divider, color_3: e.target.value })}
                className="mt-1"
                placeholder="#4ade80"
              />
            </div>
          </div>

          <Button
            onClick={() => saveSection('divider', divider)}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} />
            Save Divider
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activities Carousel</CardTitle>
          <CardDescription>Edit slides (image URL + title + description + link)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Auto-advance Interval (ms)</Label>
            <Input
              type="number"
              value={activities.interval_ms || 4000}
              onChange={(e) => setActivities({ ...activities, interval_ms: parseInt(e.target.value || '4000') })}
              className="mt-1"
              min={2000}
              max={12000}
            />
          </div>

          <div className="space-y-4">
            {(activities.slides || []).map((s, idx) => (
              <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-semibold">Slide {idx + 1}</p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const next = [...(activities.slides || [])];
                      next.splice(idx, 1);
                      setActivities({ ...activities, slides: next });
                    }}
                  >
                    <Trash2 size={16} className="mr-2" /> Remove
                  </Button>
                </div>

                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={s.image}
                    onChange={(e) => {
                      const next = [...(activities.slides || [])];
                      next[idx] = { ...next[idx], image: e.target.value };
                      setActivities({ ...activities, slides: next });
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={s.title}
                    onChange={(e) => {
                      const next = [...(activities.slides || [])];
                      next[idx] = { ...next[idx], title: e.target.value };
                      setActivities({ ...activities, slides: next });
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={s.description}
                    onChange={(e) => {
                      const next = [...(activities.slides || [])];
                      next[idx] = { ...next[idx], description: e.target.value };
                      setActivities({ ...activities, slides: next });
                    }}
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Link</Label>
                  <Input
                    value={s.link || '/news'}
                    onChange={(e) => {
                      const next = [...(activities.slides || [])];
                      next[idx] = { ...next[idx], link: e.target.value };
                      setActivities({ ...activities, slides: next });
                    }}
                    className="mt-1"
                    placeholder="/news"
                  />
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const next = [...(activities.slides || [])];
                next.push({
                  image: '',
                  title: 'New Activity',
                  description: 'Describe this activity',
                  link: '/news',
                });
                setActivities({ ...activities, slides: next });
              }}
            >
              <Plus size={18} className="mr-2" /> Add Slide
            </Button>
          </div>

          <Button
            onClick={() => saveSection('activities', activities)}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} />
            Save Activities Carousel
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What We Stand For</CardTitle>
          <CardDescription>Heading + subheading + 3 cards (Markdown supported)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={standFor.heading}
              onChange={(e) => setStandFor({ ...standFor, heading: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Subheading (Markdown)</Label>
            <Textarea
              value={standFor.subheading}
              onChange={(e) => setStandFor({ ...standFor, subheading: e.target.value })}
              className="mt-1"
              rows={2}
            />
          </div>

          <div className="space-y-4">
            {(standFor.cards || []).map((c, idx) => (
              <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3">
                <p className="font-semibold">Card {idx + 1}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Icon</Label>
                    <select
                      value={c.icon}
                      onChange={(e) => {
                        const next = [...standFor.cards];
                        next[idx] = { ...next[idx], icon: e.target.value };
                        setStandFor({ ...standFor, cards: next });
                      }}
                      className="w-full px-3 py-2 border rounded-md mt-1"
                    >
                      {iconOptionsStandFor.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={c.title}
                      onChange={(e) => {
                        const next = [...standFor.cards];
                        next[idx] = { ...next[idx], title: e.target.value };
                        setStandFor({ ...standFor, cards: next });
                      }}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Body (Markdown)</Label>
                  <Textarea
                    value={c.body_markdown}
                    onChange={(e) => {
                      const next = [...standFor.cards];
                      next[idx] = { ...next[idx], body_markdown: e.target.value };
                      setStandFor({ ...standFor, cards: next });
                    }}
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>

          <Button
            onClick={() => saveSection('stand_for', standFor)}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save What We Stand For
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Public Sector Coverage</CardTitle>
          <CardDescription>Text + tiles + slides (image URL + icon + sector + status)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Badge</Label>
            <Input
              value={coverage.badge}
              onChange={(e) => setCoverage({ ...coverage, badge: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Heading</Label>
            <Input
              value={coverage.heading}
              onChange={(e) => setCoverage({ ...coverage, heading: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Intro</Label>
            <Textarea
              value={coverage.intro}
              onChange={(e) => setCoverage({ ...coverage, intro: e.target.value })}
              className="mt-1"
              rows={2}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="border rounded-xl p-4 bg-gray-50 space-y-2">
              <p className="font-semibold">Tile: Sector</p>
              <Input
                value={coverage.tiles?.sector?.title}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, sector: { ...coverage.tiles.sector, title: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.sector?.value}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, sector: { ...coverage.tiles.sector, value: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.sector?.caption}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, sector: { ...coverage.tiles.sector, caption: e.target.value } },
                  })
                }
              />
            </div>
            <div className="border rounded-xl p-4 bg-gray-50 space-y-2">
              <p className="font-semibold">Tile: Reach</p>
              <Input
                value={coverage.tiles?.reach?.title}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, reach: { ...coverage.tiles.reach, title: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.reach?.value}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, reach: { ...coverage.tiles.reach, value: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.reach?.caption}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, reach: { ...coverage.tiles.reach, caption: e.target.value } },
                  })
                }
              />
            </div>
            <div className="border rounded-xl p-4 bg-gray-50 space-y-2">
              <p className="font-semibold">Tile: Focus</p>
              <Input
                value={coverage.tiles?.focus?.title}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, focus: { ...coverage.tiles.focus, title: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.focus?.value}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, focus: { ...coverage.tiles.focus, value: e.target.value } },
                  })
                }
              />
              <Input
                value={coverage.tiles?.focus?.caption}
                onChange={(e) =>
                  setCoverage({
                    ...coverage,
                    tiles: { ...coverage.tiles, focus: { ...coverage.tiles.focus, caption: e.target.value } },
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-4">
            {(coverage.slides || []).map((s, idx) => (
              <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">Slide {idx + 1}</p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const next = [...(coverage.slides || [])];
                      next.splice(idx, 1);
                      setCoverage({ ...coverage, slides: next });
                    }}
                  >
                    <Trash2 size={16} className="mr-2" /> Remove
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Sector</Label>
                    <Input
                      value={s.sector}
                      onChange={(e) => {
                        const next = [...coverage.slides];
                        next[idx] = { ...next[idx], sector: e.target.value };
                        setCoverage({ ...coverage, slides: next });
                      }}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Input
                      value={s.status}
                      onChange={(e) => {
                        const next = [...coverage.slides];
                        next[idx] = { ...next[idx], status: e.target.value };
                        setCoverage({ ...coverage, slides: next });
                      }}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Icon</Label>
                    <select
                      value={s.icon}
                      onChange={(e) => {
                        const next = [...coverage.slides];
                        next[idx] = { ...next[idx], icon: e.target.value };
                        setCoverage({ ...coverage, slides: next });
                      }}
                      className="w-full px-3 py-2 border rounded-md mt-1"
                    >
                      {iconOptionsCoverage.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      value={s.image}
                      onChange={(e) => {
                        const next = [...coverage.slides];
                        next[idx] = { ...next[idx], image: e.target.value };
                        setCoverage({ ...coverage, slides: next });
                      }}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={s.description}
                    onChange={(e) => {
                      const next = [...coverage.slides];
                      next[idx] = { ...next[idx], description: e.target.value };
                      setCoverage({ ...coverage, slides: next });
                    }}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const next = [...(coverage.slides || [])];
                next.push({
                  sector: 'New Sector',
                  status: 'Eligible',
                  description: 'Describe this sector coverage',
                  image: '',
                  icon: 'Building2',
                });
                setCoverage({ ...coverage, slides: next });
              }}
            >
              <Plus size={18} className="mr-2" /> Add Slide
            </Button>
          </div>

          <Button
            onClick={() => saveSection('coverage', coverage)}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Public Sector Coverage
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Call to Action (CTA)</CardTitle>
          <CardDescription>Heading + body (Markdown) + button</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={cta.heading}
              onChange={(e) => setCTA({ ...cta, heading: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              value={cta.body_markdown}
              onChange={(e) => setCTA({ ...cta, body_markdown: e.target.value })}
              className="mt-1"
              rows={3}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={cta.button_text}
                onChange={(e) => setCTA({ ...cta, button_text: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={cta.button_link}
                onChange={(e) => setCTA({ ...cta, button_link: e.target.value })}
                className="mt-1"
                placeholder="/membership"
              />
            </div>
          </div>

          <Button
            onClick={() => saveSection('cta', cta)}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save CTA
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeSectionsEditor;
